import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import * as authActions from './auth.actions';
import * as sessionActions from '../../store/session.actions';
import * as clientsActions from '../../clients/client-list/store/clients.actions';
import { switchMap, map, withLatestFrom, mergeMap, catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { AuthService, ClientsSelectors } from '../../store';
import { Router } from '@angular/router';
import { OpdateringNoterComponent } from '../../core/opdatering-noter/opdatering-noter.component';
import { OpdateringNoterService } from '../../core/opdatering-noter/opdatering-noter.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MenuService } from '../../notifications/modals/menu.service';

@Injectable()
export class AuthEffects {


     autoLogin$ = createEffect(() => this.actions$.pipe(
        ofType(authActions.ActionTypes.AutoLogin),
        map(() => localStorage.getItem('token')),
        mergeMap((token) => this.authService.loginWithToken(token).pipe(
            map((user) => {
                if (!user) {
                    localStorage.removeItem('token');
                    this.router.navigate(['/login']);
                    return new authActions.AdLogin();
                }
                return new authActions.LoginSuccess(user);
            }),
            catchError(() => {
                localStorage.removeItem('token');
                this.router.navigate(['/login']);
                return of(new authActions.AdLogin());
            })
        ))
    ));

     adAuth$ = createEffect(() => this.actions$.pipe(
        ofType(authActions.ActionTypes.AdLogin),
        mergeMap(() => this.authService.adAuth().pipe(
            map((user) => {
                if (user.indexCard !== null) {
                    localStorage.setItem('token', user.token);
                    return new authActions.LoginSuccess(user);
                } else {
                    return new authActions.AdLoginFailed();
                }
            }),
            catchError(() => of(new authActions.AdLoginFailed()))
        ))
    ));

     impersonate$ = createEffect(() => this.actions$.pipe(
        ofType<authActions.Impersonate>(authActions.ActionTypes.Impersonate),
        map(action => action.payload),
        mergeMap((username) => this.authService.impersonate(username).pipe(
            map((user) => {
                if (!user) {
                    return new authActions.LoginFailed('Brugerskift fejlede!');
                }
                localStorage.setItem('token', user.token);
                localStorage.setItem('impersonating', 'true');
                this.menuService.destroyStatistikModal();
                return new authActions.LoginSuccess(user);
            }),
            catchError(() => of(new authActions.LoginFailed('Der opstod en fejl i login. Der er muligvis ingen forbindelse til serveren.')))
        ))
    ));

     login$ = createEffect(() => this.actions$.pipe(
        ofType<authActions.Login>(authActions.ActionTypes.Login),
        map(action => action.payload),
        mergeMap((login) => this.authService.login(login.username, login.password).pipe(
            map((user) => {
                if (!user) {
                    return new authActions.LoginFailed('Brugernavn og/eller adgangskode er forkert.');
                }
                localStorage.setItem('token', user.token);
                return new authActions.LoginSuccess(user);
            }),
            catchError(() => of(new authActions.LoginFailed('Der opstod en fejl i login. Der er muligvis ingen forbindelse til serveren.')))
        ))
    ));

    loginMsal$ = createEffect(() => this.actions$.pipe(
        ofType<authActions.LoginMsal>(authActions.ActionTypes.LoginMsal),
        mergeMap(() => this.authService.msalLogin().pipe(
            map((user) => {
                if (!user) {
                    return new authActions.LoginFailed('Brugernavn og/eller adgangskode er forkert.');
                }
                return new authActions.LoginSuccess(user);
            }),
            catchError((err) => {
                if (err.status === 401) {
                    return of(new authActions.LoginFailed('Brugeren har ikke adgang til denne tjeneste.'));
                }
                return of(new authActions.LoginFailed('Der opstod en fejl i login. Der er muligvis ingen forbindelse til serveren.'));
            }
            )
        ))
    ));

     loginSuccess$ = createEffect(() => this.actions$.pipe(
        ofType<authActions.LoginSuccess>(authActions.ActionTypes.LoginSuccess),
        map(action => action.payload),
        switchMap((user) => {
            const splitUserToken = user.token?.split('.')[1];
            const unparsedRedirection = splitUserToken ? atob(splitUserToken) : undefined;
            const redirection: string = unparsedRedirection ? JSON.parse(unparsedRedirection)?.redirection : undefined;
            if (redirection) {
                window.location.replace(redirection);
            }
            return [
                new authActions.LoadOfficeSettings(),
                new authActions.LoadHasNavision(),
                new authActions.LoadRoleSettings(),
                authActions.opdateringNoter(),
                new sessionActions.LoadMenu(),
                new sessionActions.LoadUsers(),
                new sessionActions.LoadRecentClients(),
                new clientsActions.LoadClientColumns(),
                new clientsActions.LoadClientSort(),
                new clientsActions.LoadClientFilter(),
                new clientsActions.LoadClients()
            ];
        }),
        tap(() => this.router.navigate(['/']))
    ));

     logout$ = createEffect(() => this.actions$.pipe(
        ofType(authActions.ActionTypes.Logout),
        tap(() => {
            const impersonating = localStorage.getItem('impersonating');
            localStorage.removeItem('impersonating');
            this.menuService.destroyStatistikModal();
            if (impersonating) {
                this.router.navigate(['/login']);
            } else {
                this.router.navigate(['/login'], {queryParams: {noautologin: 'true'}});
            }
        })
    ), {dispatch: false});

     switchOffice$ = createEffect(() => this.actions$.pipe(
        ofType(authActions.ActionTypes.SwitchOffice),
        map(() => new authActions.LoadOfficeSettings())
    ));

     loadOfficeSettings$ = createEffect(() => this.actions$.pipe(
        ofType(authActions.ActionTypes.LoadOfficeSettings),
        withLatestFrom(this.clientsSelector.clientsLoaded$),
        switchMap(([, clientsLoaded]) =>
            this.authService.getOfficeSettings().pipe(
                mergeMap((settings) => {
                    if (!clientsLoaded) {
                        return [
                            new authActions.LoadOfficeSettingsSuccess(settings),
                            new sessionActions.LoadRecentClients()
                        ];
                    }
                    return [
                        new authActions.LoadOfficeSettingsSuccess(settings),
                        new sessionActions.LoadRecentClients(),
                        new clientsActions.LoadClients()
                    ];
                })
            )
        )
    ));

     loadRoleSettings$ = createEffect(() => this.actions$.pipe(
        ofType(authActions.ActionTypes.LoadRoleSettings),
        switchMap(() => this.authService.getRoleSettings().pipe(
            map(roleSettings => new authActions.LoadRoleSettingsSuccess(roleSettings))
        ))
    ));

    opdateringNoter$ = createEffect(() => this.actions$.pipe(
        ofType(authActions.opdateringNoter),
        switchMap(() => this.opdateringNoterService.getUnread90DaysByUser().pipe(
            map(notifications => authActions.opdateringNoterShow({ noter: notifications }))
        ))
    ));

    opdateringNoterShow$ = createEffect(() => this.actions$.pipe(
        ofType(authActions.opdateringNoterShow),
        tap(props => {
            if (props.noter?.length > 0) {
                const modalRef = this.ngbModal.open(OpdateringNoterComponent, { size: 'lg', backdrop: 'static', scrollable: true });
                modalRef.componentInstance.showFooter = true;
                modalRef.componentInstance.noter = props.noter;
            }
        })
    ), { dispatch: false });

    updateOfficeSetting$ = createEffect(() => this.actions$.pipe(
        ofType(authActions.updateOfficeSetting),
        switchMap(props => this.authService.updateSetting(props.patchObject).pipe(
            map(settings => new authActions.LoadOfficeSettingsSuccess(settings))
        ))
    ));

    hasNavision$ = createEffect(() => this.actions$.pipe(
        ofType(authActions.ActionTypes.LoadHasNavision),
        switchMap(() => this.authService.getHasNavision().pipe(
            map(hasNavision => new authActions.LoadHasNavisionSuccess(hasNavision))
        ))
    ));

    constructor(
        private actions$: Actions,
        private authService: AuthService,
        private clientsSelector: ClientsSelectors,
        private router: Router,
        private opdateringNoterService: OpdateringNoterService,
        private ngbModal: NgbModal,
        private menuService: MenuService,
    ) {}

}
